import { normalizeImageSrc } from './imageUrl';
import { getApiBaseUrl } from '../config/api';
import { logCatalogImageResolve } from './catalogImageDiagnostics';
import { filterViableImageCandidates } from './failedImageUrlSession';
import {
  isPreGeneratedWebVariantUrl,
  type MockupSideVariantUrls,
  type WebImageVariantKey,
} from './webImageVariants';

export type CatalogImageProfile = 'shop-card' | 'catalog-thumb' | 'pdp-hero';
export type CatalogDeliveryMode = 'grid' | 'source' | 'hero';

type LegacyMockupVariantKey = 'thumbnail' | 'small' | 'medium';

const VARIANT_TIER_RE =
  /\/(thumb_320|card_400|hero_800|hero_1000|thumbnail|small|medium|large|original)-\d+\.(webp|png|jpe?g|avif)$/i;

const EXPLICIT_VARIANT_ORDER: Record<
  CatalogImageProfile,
  Array<WebImageVariantKey | LegacyMockupVariantKey>
> = {
  'shop-card': ['card_400', 'thumb_320', 'thumbnail', 'small', 'medium'],
  'catalog-thumb': ['thumb_320', 'thumbnail', 'card_400', 'small'],
  'pdp-hero': [
    'hero_800',
    'hero_1000',
    'small',
    'medium',
    'card_400',
    'thumb_320',
    'thumbnail',
  ],
};

const GRID_PROFILE_WIDTH: Record<CatalogImageProfile, number> = {
  'shop-card': 400,
  'catalog-thumb': 320,
  'pdp-hero': 800,
};

const GRID_PROFILE_HEIGHT: Record<CatalogImageProfile, number> = {
  'shop-card': 533,
  'catalog-thumb': 427,
  'pdp-hero': 1000,
};

const HERO_THUMB_MAX_WIDTH = 1000;
const HERO_THUMB_FALLBACK_WIDTH = 800;

const PREVIEW_THUMB_WIDTH: Record<CatalogImageProfile, number> = {
  'shop-card': 56,
  'catalog-thumb': 48,
  'pdp-hero': 96,
};

export function isCatalogThumbApiUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return /\/media\/catalog-thumb\?/i.test(url);
}

export function parseMockupVariantTier(url: string): string | null {
  const match = url.match(VARIANT_TIER_RE);
  return match ? match[1].toLowerCase() : null;
}

/**
 * Print / fulfillment assets only — never block design-mockups web variants.
 */
export function isPrintResolutionAssetUrl(url: string): boolean {
  const lower = url.toLowerCase();
  if (/\b3600x4800\b|\b4800x3600\b/.test(lower)) return true;
  if (/\/print[-_/]/.test(lower)) return true;
  if (/\/design-export(?:s)?\//.test(lower)) return true;
  return false;
}

export function optimizeExternalImageUrl(url: string): string {
  if (/i\.pinimg\.com\/1200x\//i.test(url)) {
    return url.replace('/1200x/', '/736x/');
  }
  return url;
}

/** Pre-generated CDN web variants and /product-catalog/web/ paths skip runtime thumb. */
function isHeavyProductCatalogRaster(url: string): boolean {
  if (!/\/product-catalog\//i.test(url)) return false;
  return /\.(png|jpe?g)$/i.test(url) || /\.png\.png$/i.test(url);
}

/** Public grids must not use multi-MB catalog PNGs as `<img src>`. */
function shouldAllowRawGridFallback(
  url: string,
  profile: CatalogImageProfile,
): boolean {
  if (profile === 'pdp-hero') return true;
  if (isHeavyProductCatalogRaster(url)) return false;
  return true;
}

export function shouldRouteThroughCatalogThumb(url: string): boolean {
  if (isPrintResolutionAssetUrl(url)) return false;
  if (isCatalogThumbApiUrl(url)) return false;
  if (isPreGeneratedWebVariantUrl(url)) return false;

  if (/\/product-catalog\/web\//i.test(url)) return false;

  if (/\/product-catalog\//i.test(url)) {
    if (/\.(webp|avif)$/i.test(url) && !/\.png\.(webp|png)$/i.test(url)) {
      return false;
    }
    return /\.(png|jpe?g)$/i.test(url) || /\.png\.png$/i.test(url);
  }

  if (/\/design-mockups\//i.test(url)) {
    const tier = parseMockupVariantTier(url);
    if (!tier) return true;
    return tier === 'large' || tier === 'original';
  }

  if (/\/(design-previews|custom-mockups)\//i.test(url)) {
    if (isPreGeneratedWebVariantUrl(url)) return false;
    if (/\/web\//i.test(url)) return false;
    return true;
  }

  return false;
}

/** Known high-res source for catalog-thumb — explicit map only, never guessed paths. */
export function resolveCatalogThumbSourceUrl(
  raw: string | null | undefined,
  mockupVariants?: MockupSideVariantUrls,
): string | null {
  const fromVariants =
    normalizeImageSrc(mockupVariants?.large) ||
    normalizeImageSrc(mockupVariants?.original);
  if (fromVariants && !isPrintResolutionAssetUrl(fromVariants)) {
    return fromVariants;
  }

  const normalized = normalizeImageSrc(raw);
  if (!normalized || isPrintResolutionAssetUrl(normalized)) return null;
  if (shouldRouteThroughCatalogThumb(normalized)) return normalized;
  return fromVariants ?? normalized;
}

export function resolveCatalogThumbWebUrl(
  raw: string | null | undefined,
  width = 400,
  mockupVariants?: MockupSideVariantUrls,
): string | null {
  const source = resolveCatalogThumbSourceUrl(raw, mockupVariants);
  if (!source || !shouldRouteThroughCatalogThumb(source)) return null;

  const targetWidth = Math.min(
    Math.max(Math.round(width) || 400, 120),
    HERO_THUMB_MAX_WIDTH,
  );
  return `${getApiBaseUrl()}/media/catalog-thumb?width=${targetWidth}&src=${encodeURIComponent(source)}`;
}

/** Tiny blur-up preview — explicit variants or safe catalog-thumb only. */
export function resolveMockupPreviewUrl(
  raw: string | null | undefined,
  profile: CatalogImageProfile = 'shop-card',
  mockupVariants?: MockupSideVariantUrls,
): string | null {
  if (mockupVariants?.thumb_320) {
    return normalizeImageSrc(mockupVariants.thumb_320);
  }
  if (profile === 'pdp-hero' && mockupVariants?.hero_800) {
    return normalizeImageSrc(mockupVariants.hero_800);
  }
  if (mockupVariants?.card_400) {
    return normalizeImageSrc(mockupVariants.card_400);
  }

  return resolveCatalogThumbWebUrl(
    raw,
    PREVIEW_THUMB_WIDTH[profile],
    mockupVariants,
  );
}

function collectExplicitVariantCandidates(
  profile: CatalogImageProfile,
  mockupVariants?: MockupSideVariantUrls,
): string[] {
  if (!mockupVariants) return [];

  const out: string[] = [];
  const push = (value: string | null | undefined) => {
    const next = normalizeImageSrc(value);
    if (!next || isPrintResolutionAssetUrl(next) || out.includes(next)) return;
    out.push(next);
  };

  for (const key of EXPLICIT_VARIANT_ORDER[profile]) {
    push(mockupVariants[key]);
  }

  return out;
}

export type CatalogImageDelivery = {
  src: string;
  srcSet?: string;
  sizes?: string;
  width: number;
  height: number;
  candidates: string[];
};

export function buildCatalogImageDelivery(
  raw: string | null | undefined,
  profile: CatalogImageProfile = 'shop-card',
  mode: CatalogDeliveryMode = 'grid',
  mockupVariants?: MockupSideVariantUrls,
): CatalogImageDelivery | null {
  const normalized = normalizeImageSrc(raw);
  if (!normalized || isPrintResolutionAssetUrl(normalized)) return null;

  const width = GRID_PROFILE_WIDTH[profile];
  const height = GRID_PROFILE_HEIGHT[profile];

  if (mode === 'source' || isCatalogThumbApiUrl(normalized)) {
    return {
      src: normalized,
      width,
      height,
      candidates: [normalized],
    };
  }

  const candidates: string[] = [];
  const push = (value: string | null | undefined) => {
    const next = normalizeImageSrc(value);
    if (!next || isPrintResolutionAssetUrl(next) || candidates.includes(next)) {
      return;
    }
    candidates.push(next);
  };

  for (const explicit of collectExplicitVariantCandidates(
    profile,
    mockupVariants,
  )) {
    push(explicit);
  }

  if (
    isPreGeneratedWebVariantUrl(normalized) &&
    !candidates.includes(normalized)
  ) {
    push(normalized);
  }

  const thumbWidth =
    profile === 'pdp-hero' ? HERO_THUMB_FALLBACK_WIDTH : width;
  const thumbUrl = resolveCatalogThumbWebUrl(
    normalized,
    thumbWidth,
    mockupVariants,
  );
  const thumbWide =
    profile === 'pdp-hero'
      ? resolveCatalogThumbWebUrl(
          normalized,
          HERO_THUMB_MAX_WIDTH,
          mockupVariants,
        )
      : profile === 'shop-card'
        ? resolveCatalogThumbWebUrl(normalized, 480, mockupVariants)
        : null;

  if (thumbUrl) {
    push(thumbUrl);
  }

  if (normalized !== thumbUrl && shouldAllowRawGridFallback(normalized, profile)) {
    push(normalized);
  }

  if (!candidates.length) return null;

  const viable = filterViableImageCandidates(candidates);
  if (!viable.length) return null;

  const primaryIsThumb = isCatalogThumbApiUrl(viable[0]);
  const srcSetParts: string[] = [];
  if (primaryIsThumb && thumbWide && thumbWide !== viable[0]) {
    srcSetParts.push(`${viable[0]} ${thumbWidth}w`, `${thumbWide} ${HERO_THUMB_MAX_WIDTH}w`);
  }

  return {
    src: viable[0],
    srcSet: srcSetParts.length ? srcSetParts.join(', ') : undefined,
    sizes:
      srcSetParts.length > 0
        ? profile === 'pdp-hero'
          ? '(max-width: 1024px) 85vw, 640px'
          : '(max-width: 640px) 46vw, (max-width: 1024px) 33vw, 320px'
        : undefined,
    width,
    height,
    candidates: viable,
  };
}

/**
 * Ordered display candidates:
 * explicit API variants → catalog-thumb → known source URL.
 * Never guesses CDN filenames.
 */
export function buildCatalogImageCandidateList(
  sources: Array<string | null | undefined>,
  profile: CatalogImageProfile = 'shop-card',
  mode: CatalogDeliveryMode = 'grid',
  mockupVariants?: MockupSideVariantUrls,
): string[] {
  const out: string[] = [];
  const push = (value: string | null | undefined) => {
    const next = normalizeImageSrc(value);
    if (!next || isPrintResolutionAssetUrl(next) || out.includes(next)) return;
    out.push(next);
  };

  for (const raw of sources) {
    const delivery = buildCatalogImageDelivery(
      raw,
      profile,
      mode,
      mockupVariants,
    );
    if (delivery) {
      for (const candidate of delivery.candidates) push(candidate);
      continue;
    }
    push(raw);
  }

  return filterViableImageCandidates(out);
}

export function collectCatalogImageCandidates(
  raw: string | null | undefined,
  profile: CatalogImageProfile = 'shop-card',
  mode: CatalogDeliveryMode = 'grid',
  mockupVariants?: MockupSideVariantUrls,
): string[] {
  return buildCatalogImageCandidateList([raw], profile, mode, mockupVariants);
}

export function logCatalogImageFallback(params: {
  surface: string;
  entityId?: string | null;
  sourceUrl?: string | null;
  attemptedUrl?: string | null;
  nextUrl?: string | null;
  reason: string;
}): void {
  logCatalogImageResolve({
    surface: params.surface,
    entityId: params.entityId,
    sourceUrl: params.sourceUrl,
    thumbUrl: isCatalogThumbApiUrl(params.attemptedUrl)
      ? params.attemptedUrl
      : undefined,
    fallbackUrl: params.nextUrl,
    reason: params.reason,
  });
}
